import { useState } from "react";
import Alert from "@iscr/views/NUI/Alert";
import { Checkbox, Dropdown, Header as Heading } from "semantic-ui-react";
import { useNavigate } from "react-router";
import { AsyncButton } from "@iscr/views/UIelems/AsyncButton";
import { rejectOnResErr } from "@iscr/xAppLib/helpers/rejectOnResErr";
import { useAsyncUpdate } from "@iscr/xAppLib/Hooks/useAsync";
import API_service from "@iscr/xAppLib/providers/API_service";
import statsig from '@iscr/xAppLib/providers/statsig';
import { useSiteStatus, useExperiment } from "@iscr/xAppLib/Hooks";
import { WAIT_TIME_EXP } from "@iscr/views/pages/homepage/consts";

export const PATIENT_CANCEL_REASONS = [
	'I accidentally made a duplicate booking',
	'I entered the wrong information into the booking',
	'I selected the wrong patient or family member',
	'I have been waiting too long',
	'I am having technical difficulties',
	'I found a cheaper service',
	'My condition has improved',
	'I have sought help elsewhere',
	'Other',
].map(r => ({ key: r, value: r, text: r }));

export function CancelReasonSelector({ onChange, showConfirmation = true }) {
	const [reason, setReason] = useState('');
	const [otherText, setOtherText] = useState('');
	const [confirmed, setConfirmed] = useState(false);

	const isOther = reason === 'Other';

	const notify = (newReason, newOtherText, newConfirmed) => {
		const isValid = Boolean(newReason && (newReason !== 'Other' || newOtherText.trim()) && (!showConfirmation || newConfirmed));
		onChange?.({
			cancelReason: newReason,
			cancelOtherText: newOtherText.trim(),
			isValid
		});
	};

	const handleReasonChange = (value) => {
		setReason(value);
		setOtherText('');
		notify(value, '', confirmed);
	};

	const handleOtherTextChange = (text) => {
		setOtherText(text);
		notify(reason, text, confirmed);
	};

	const handleConfirmedChange = (checked) => {
		setConfirmed(checked);
		notify(reason, otherText, checked);
	};

	return (
		<div className="space-y-4">
			<div className="space-y-2">
				<label className="block font-medium">
					Please let us know why you want to cancel this request
				</label>
				<Dropdown
					fluid
					selection
					options={PATIENT_CANCEL_REASONS}
					value={reason}
					placeholder="Select a reason for cancellation"
					onChange={(_, { value }) => handleReasonChange(value)}
					data-testid="dropdown-cancel-reason"
				/>
			</div>

			{isOther && (
				<div className="space-y-2">
					<label className="block font-medium">
						Please provide more details
					</label>
					<textarea
						className="w-full border border-gray-300 rounded px-3 py-2"
						rows={3}
						value={otherText}
						onChange={e => handleOtherTextChange(e.target.value)}
						placeholder="Please tell us more..."
					/>
				</div>
			)}

			{showConfirmation && (
				<Checkbox
					label="I confirm that I want to proceed with a cancellation"
					checked={confirmed}
					onChange={(_, { checked }) => handleConfirmedChange(checked)}
					data-testid="checkbox-cancel-confirm"
				/>
			)}
		</div>
	);
}

export function CancelConsult({data, order, withReason}) {
	const sid = data.sid;
	const navigate = useNavigate();
	const mobilehash = order?.data?.spd_hash__mobile;

	const [showReason, setShowReason] = useState(false);
	const [reasonState, setReasonState] = useState({ cancelReason: '', cancelOtherText: '', isValid: false });

	const canCancel = Boolean(sid && mobilehash) && (!withReason || reasonState.isValid);
	const waitTimeExp = useExperiment(WAIT_TIME_EXP, { quiet: true })?.n;
	const site_status = useSiteStatus();
	const avg_wait = site_status?.wr_stat?.prediction?.data?.pc_98_wait;

	const cancel = useAsyncUpdate(async () => {
		await API_service.load_data(
			'doccons_req',
			{
				act: 'cancel_consult',
				sid,
				mobilehash,
				...(withReason && {
					cancel_reason: reasonState.cancelReason,
					...(reasonState.cancelReason === 'Other' && { cancel_other_text: reasonState.cancelOtherText }),
					cancel_initiated_by: 'patient',
				}),
			},
		).then(rejectOnResErr);

		waitTimeExp && statsig.log_event('cancel', null, { time: avg_wait });
		app.user.reload_profile();
		navigate("/");
	});

	return (
		<>
			<Heading className="font-bold text-2xl">
				Cancel Consultation Request
			</Heading>
			<div className="space-y-6">
				{withReason && !showReason
					? <AsyncButton
						content="Cancel Consultation"
						color="red"
						size="large"
						disabled={!Boolean(sid && mobilehash)}
						data-testid="button-cancel"
						onClick={() => setShowReason(true)}
					/>
					: <>
						{withReason && <CancelReasonSelector onChange={setReasonState} />}

						<div className="mt-4">
							{cancel.error && (
								<Alert error header="Error cancelling consult">
									{String(cancel.error?.err ?? cancel.error?.error ?? cancel.error?.message ?? cancel.error)}
								</Alert>
							)}
							<AsyncButton
								content="Cancel Consultation"
								color="red"
								size="large"
								disabled={!canCancel}
								data-testid="button-cancel"
								onClick={cancel.fn}
							/>
						</div>
					</>
				}
			</div>
		</>
	);
}
